import { useEffect, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Upload, Eye, Trash2, FileText, ImageOff, Loader2, FileDown } from "lucide-react";

const KINDS = [
  { key: "internal", label: "Internal Label" },
  { key: "external", label: "External Label" },
];

export function ProductLabels({ product, canManage, onChanged }) {
  const [previews, setPreviews] = useState({});
  const [busy, setBusy] = useState("");
  const [removing, setRemoving] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const inputs = useRef({});
  const pid = product?.id;
  const intPath = product?.labels?.internal?.path;
  const extPath = product?.labels?.external?.path;

  useEffect(() => {
    if (!pid) return;
    let alive = true;
    const made = [];
    (async () => {
      const next = {};
      for (const { key } of KINDS) {
        const meta = product?.labels?.[key];
        if (!meta?.path) continue;
        try {
          const res = await api.get(`/products/${pid}/labels/${key}`, { responseType: "blob" });
          const url = URL.createObjectURL(res.data);
          made.push(url);
          next[key] = { url, meta };
        } catch {
          next[key] = { url: null, meta };
        }
      }
      if (alive) setPreviews(next);
      else made.forEach((u) => URL.revokeObjectURL(u));
    })();
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, intPath, extPath]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(`upload-${kind}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/products/${pid}/labels/${kind}`, fd);
      toast.success(`${kind === "internal" ? "Internal" : "External"} label uploaded`);
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally {
      setBusy("");
      if (inputs.current[kind]) inputs.current[kind].value = "";
    }
  };

  const remove = async () => {
    const kind = removing;
    setRemoving(null);
    setBusy(`remove-${kind}`);
    try {
      await api.delete(`/products/${pid}/labels/${kind}`);
      toast.success("Label removed");
      onChanged?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Remove failed"));
    } finally {
      setBusy("");
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/products/${pid}/master-data-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${product?.productId || "product"}-master-data.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e, "PDF download failed"));
    } finally {
      setPdfBusy(false);
    }
  };

  if (!product) return null;

  return (
    <div data-testid="product-labels-panel" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-stone-500">Product Labels &amp; Master Data</div>
        <Button size="sm" variant="outline" data-testid="master-data-pdf-btn" disabled={pdfBusy} onClick={downloadPdf} className="h-8 gap-1.5">
          {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Master Data PDF
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {KINDS.map(({ key, label }) => {
          const p = previews[key];
          const meta = product?.labels?.[key];
          const isImg = String(meta?.contentType || "").startsWith("image/");
          return (
            <div key={key} className="rounded-lg border border-stone-200 p-2.5">
              <div className="mb-2 text-xs font-semibold text-stone-700">{label}</div>
              <button type="button" data-testid={`label-thumb-${key}`} disabled={!p?.url}
                onClick={() => p?.url && window.open(p.url, "_blank")}
                className="flex h-28 w-full items-center justify-center overflow-hidden rounded-md border border-dashed border-stone-200 bg-stone-50">
                {p?.url && isImg ? (
                  <img src={p.url} alt={label} className="h-full w-full object-contain" />
                ) : p?.url ? (
                  <span className="flex flex-col items-center gap-1 text-stone-500"><FileText className="h-6 w-6" /><span className="px-1 text-center text-[10px]">{meta?.filename}</span></span>
                ) : meta ? (
                  <Loader2 className="h-5 w-5 animate-spin text-stone-300" />
                ) : (
                  <span className="flex flex-col items-center gap-1 text-stone-300"><ImageOff className="h-6 w-6" /><span className="text-[10px]">No label uploaded</span></span>
                )}
              </button>
              <div className="mt-2 flex items-center gap-1">
                {canManage && (
                  <>
                    <input ref={(el) => (inputs.current[key] = el)} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf" className="hidden"
                      data-testid={`label-file-${key}`} onChange={(e) => upload(key, e.target.files?.[0])} />
                    <Button size="sm" variant="outline" data-testid={`label-upload-${key}`} disabled={!!busy}
                      className="h-7 flex-1 gap-1 text-[11px]" onClick={() => inputs.current[key]?.click()}>
                      {busy === `upload-${key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                      {meta ? "Replace" : "Upload"}
                    </Button>
                  </>
                )}
                {meta && (
                  <Button size="sm" variant="ghost" data-testid={`label-view-${key}`} className="h-7 px-2 text-stone-500" title="View"
                    onClick={() => previews[key]?.url && window.open(previews[key].url, "_blank")}>
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canManage && meta && (
                  <Button size="sm" variant="ghost" data-testid={`label-remove-${key}`} className="h-7 px-2 text-rose-500" title="Remove"
                    onClick={() => setRemoving(key)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removing === "internal" ? "internal" : "external"} label?</AlertDialogTitle>
            <AlertDialogDescription>The label is detached from this product. No other product data is affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="label-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="label-remove-confirm" className="bg-rose-600 hover:bg-rose-700" onClick={remove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
